import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { Story, Vote } from '../types'
import { calculateStatistics } from './statistics'

interface ExportData {
  roomName: string
  stories: Story[]
  votesByStory: Record<string, Vote[]>
}

export function exportToPdf({ roomName, stories, votesByStory }: ExportData): void {
  const completedStories = stories.filter((s) => s.status === 'completed')

  if (completedStories.length === 0) {
    alert('No completed stories to export')
    return
  }

  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()

  // Title
  doc.setFontSize(20)
  doc.setTextColor(79, 70, 229) // Primary color
  doc.text('Planning Poker Results', pageWidth / 2, 20, { align: 'center' })

  // Room name
  doc.setFontSize(14)
  doc.setTextColor(55, 65, 81)
  doc.text(roomName, pageWidth / 2, 30, { align: 'center' })

  // Date
  doc.setFontSize(10)
  doc.setTextColor(107, 114, 128)
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 38, { align: 'center' })

  // Summary table
  const tableData = completedStories.map((story) => {
    const votes = votesByStory[story.id] || []
    const stats = calculateStatistics(votes)
    const voterDetails = votes.map((v) => `${v.user_name}: ${v.value}`).join(', ')

    return [
      story.title,
      story.points || '-',
      stats.average?.toString() || '-',
      stats.totalVotes.toString(),
      voterDetails || 'No votes',
    ]
  })

  autoTable(doc, {
    startY: 45,
    head: [['Story', 'Points', 'Average', 'Votes', 'Participants']],
    body: tableData,
    headStyles: {
      fillColor: [79, 70, 229],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 'auto' },
    },
  })

  // Summary stats
  const totalStories = completedStories.length
  const totalPoints = completedStories.reduce((sum, s) => {
    const points = parseFloat(s.points || '0')
    return sum + (isNaN(points) ? 0 : points)
  }, 0)

  const finalY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 15

  doc.setFontSize(12)
  doc.setTextColor(55, 65, 81)
  doc.text(`Total Stories: ${totalStories}`, 14, finalY)
  doc.text(`Total Points: ${totalPoints}`, 14, finalY + 7)

  // Footer
  doc.setFontSize(8)
  doc.setTextColor(156, 163, 175)
  doc.text('Generated by Planning Poker', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
    align: 'center',
  })

  // Save
  doc.save(`planning-poker-${roomName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`)
}
